'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  EntityRatingCommentCount,
  Professional,
  ProfessionalSchedule,
  ProfessionalScheduleProcedures,
} from '@medprev-web-app/api-graphql-client';
import { useCallback } from 'react';

import { ProfessionalAsideCard } from './professional-aside-card.component';
import { ProfessionalHeader } from './professional-header.component';
import { ProfessionalSpecialtiesAndLicense } from './professional-specialties-and-license.component';
import { ProfessionalProfileLoading } from './professional-profile-loading.component';
import { TabProfessional } from './tab-professional.component';

import { EntityCommentsSort } from '@/app/(main)/(pages)/_components/entities/entity-comments-sort.component';
import { EntityProcedureAnchorButton } from '@/app/(main)/(pages)/_components/entities/entity-procedure/_components/entity-procedure-anchor-button.component';
import { EntityScheduleRedirectButton } from '@/app/(main)/(pages)/_components/entities/entity-schedule-redirect-button.component';
import { EntityActionLoading } from '@/app/(main)/(pages)/_components/entities/entity-action-loading.component';
import { EntitySpecialtiesAndRqe } from '@/app/(main)/(pages)/_components/entities/entity-specialties-and-rqe.component';
import { mapProfessionalLicenseRows } from '@/utils/professional/professional-license.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { RatingComment } from '@/types';
import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { Card } from '@/components/ui/card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import ProfessionalNotFound from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_components/professional-not-found.component';
import { ProfessionalSchedulesSection } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_components/professional-schedules-section.component';
import { ENTITY_PROCEDURE_ASIDE_SECTION_ID } from '@/app/(main)/(pages)/_components/entities/entity-procedure/entity-procedure.const';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { useEntityComments } from '@/app/(main)/(pages)/_components/entities/hooks/use-entity-comments.hook';
import { useProfessionalProcedures } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-procedures.hook';
import { useProfessionalProfile } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-profile.hook';
import { getProfessionalProfileComments } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';
import { SearchApiRatingCommentsOrderBy } from '@/app/(main)/(pages)/_types/search-api-response.type';

interface ProfessionalWrapperProps {
  slug: string;
  professional: Professional;
  commentsCount?: EntityRatingCommentCount | null;
  procedures: ProfessionalScheduleProcedures[];
  inPersonData: ProfessionalSchedule[];
  telehealthData: ProfessionalSchedule[];
  initialComments?: RatingComment[];
}

export function ProfessionalWrapper({
  slug,
  professional: professionalFromBff,
  commentsCount: commentsCountFromBff,
  procedures,
  inPersonData,
  telehealthData,
  initialComments: initialCommentsFromBff,
}: ProfessionalWrapperProps) {
  const { value: newScheduleEnabled, loading: isFlagLoading } = useFeatureFlag('newSchedule');
  const usesNewSchedule = !isFlagLoading && newScheduleEnabled;
  const { profile, status } = useProfessionalProfile(slug, usesNewSchedule, {
    professional: professionalFromBff,
    commentsCount: commentsCountFromBff,
    inPersonData,
    telehealthData,
    initialComments: initialCommentsFromBff,
  });
  const professionalId = String(profile?.professional.id ?? '');
  const professionalProcedures = useProfessionalProcedures(professionalId, {
    enabled: usesNewSchedule && Boolean(professionalId),
  });

  const {
    orderBy,
    comments,
    hasMore,
    commentsKey,
    isSorting,
    changeOrderBy,
  } = useEntityComments(
    useCallback(
      (page: number, nextOrderBy: SearchApiRatingCommentsOrderBy) =>
        getProfessionalProfileComments(professionalId, page, nextOrderBy),
      [professionalId],
    ),
    profile?.initialComments ?? [],
    Boolean(profile?.hasMoreComments),
    { name: 'useProfessionalComments', context: { professionalId } },
  );

  const loadCommentsPage = useCallback(
    (page: number) => getProfessionalProfileComments(professionalId, page, orderBy),
    [professionalId, orderBy],
  );

  if (status === 'loading') {
    return <ProfessionalProfileLoading />;
  }

  if (status === 'error' || !profile) {
    return <ProfessionalNotFound />;
  }

  const {
    professional,
    gender,
    commentsCount,
    inPersonSchedules,
    hasTelehealth,
  } = profile;
  const hasSchedulesSection = professionalProcedures.isLoading || professionalProcedures.availableChannels.length > 0;

  const specialties = usesNewSchedule
    ? (
        <EntitySpecialtiesAndRqe
          professional={{
            licenses: mapProfessionalLicenseRows(professional),
            gender: gender ?? undefined,
            honorific: professional.honorific ?? undefined,
          }}
        />
      )
    : <ProfessionalSpecialtiesAndLicense professional={professional} />;

  const resolvedScheduleAction = usesNewSchedule
    ? hasSchedulesSection && (
      <EntityProcedureAnchorButton
        className="flex-1 min-w-0 h-10 text-base"
        data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
      />
    )
    : (
        <EntityScheduleRedirectButton
          path={`/professionals/${professionalId}/`}
          label="Consultas e horários"
          dataTestId={DataTestIdEnum.PROFESSIONAL_PROFILE_SCHEDULE_BUTTON_HEADER}
          analyticsEvent={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
          className="flex-1 min-w-0 h-10 text-base"
        />
      );

  const scheduleAction = isFlagLoading ? <EntityActionLoading /> : resolvedScheduleAction;

  const breadcrumbsItems = [
    {
      label: 'Profissionais',
      href: '/profissionais/',
    },
    {
      label: professionalDisplayName({ honorific: professional.honorific ?? '', name: professional.name ?? '' }),
    },
  ];

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} abbreviated />

      <ErrorBoundary errorMessage="Professional Wrapper">
        <ContentContainer className="px-0 sm:px-0 md:px-24 md:py-6">
          <div className="xl:grid xl:grid-flow-col xl:auto-cols-fr xl:items-start xl:gap-6">
            <section className="min-w-0">
              <Card className="w-full rounded-none border-0 p-6 sm:px-16 md:rounded-xxl md:border md:p-6">
                <ProfessionalHeader
                  professional={professional}
                  commentsCount={commentsCount}
                  shouldAddLightbox
                  specialties={specialties}
                  scheduleAction={scheduleAction}
                />
                <TabProfessional
                  professional={professional}
                  countComments={commentsCount}
                  inPersonSchedules={inPersonSchedules}
                  hasTelehealth={hasTelehealth}
                  initialComments={comments}
                  hasMoreComments={hasMore}
                  loadCommentsPage={usesNewSchedule ? loadCommentsPage : undefined}
                  commentsKey={usesNewSchedule ? commentsKey : undefined}
                  isSortingComments={usesNewSchedule && isSorting}
                  commentsSort={usesNewSchedule && (
                    <EntityCommentsSort
                      orderBy={orderBy}
                      onOrderByChange={changeOrderBy}
                      disabled={isSorting}
                    />
                  )}
                  schedulesSection={usesNewSchedule && (
                    <ProfessionalSchedulesSection
                      procedures={professionalProcedures}
                      className="rounded-none border-0 px-0 py-4"
                    />
                  )}
                />
              </Card>
            </section>

            {usesNewSchedule
              ? (
                  <ProfessionalSchedulesSection
                    procedures={professionalProcedures}
                    className="hidden w-full xl:sticky xl:top-6 xl:block"
                    scrollTargetId={ENTITY_PROCEDURE_ASIDE_SECTION_ID}
                  />
                )
              : (
                  <ProfessionalAsideCard
                    professional={professional}
                    procedure={procedures}
                    className="hidden xl:block"
                  />
                )}
          </div>
        </ContentContainer>
      </ErrorBoundary>
    </>
  );
}
